// src/pages/public/CategoriesPage/CategoriesPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './CategoriesPage.css';
import Header from '../../../components/layout/Header/Header';
import Footer from '../../../components/layout/Footer/Footer';
import productsService from '../../../services/products.service';
import categoriesService from '../../../services/categories.service';

const CategoriesPage = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('default');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100000 });

  useEffect(() => {
    fetchData();
  }, [categoryName]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all products
      const productsResult = await productsService.getAllProducts();
      
      // Fetch all categories
      const categoriesResult = await categoriesService.getAllCategories();
      
      if (productsResult.success && categoriesResult.success) {
        // Find current category (case-insensitive)
        const category = categoriesResult.categories.find(
          cat => cat.name.toLowerCase() === categoryName.toLowerCase()
        );
        
        setCurrentCategory(category);
        setAllCategories(categoriesResult.categories);
        
        // Filter products by category
        const filteredProducts = productsResult.products.filter(
          product => product.category?.toLowerCase() === categoryName.toLowerCase()
        );
        
        setProducts(filteredProducts);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sort products
  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      default:
        return 0;
    }
  }).filter(product => 
    product.price >= priceRange.min && product.price <= priceRange.max
  );

  const handleCategoryChange = (newCategory) => {
    navigate(`/categories/${newCategory.toLowerCase()}`);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      maximumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div className="categories-page">
        <Header />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des produits...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!currentCategory) {
    return (
      <div className="categories-page">
        <Header />
        <div className="error-container">
          <h2>Catégorie introuvable</h2>
          <p>La catégorie que vous recherchez n'existe pas.</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Retour à l'accueil
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="categories-page">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="category-hero"
        style={{
          background: `linear-gradient(135deg, ${currentCategory.color}22 0%, ${currentCategory.color}11 100%)`
        }}
      >
        <div className="container">
          <div className="category-hero-content">
            <div 
              className="category-hero-icon"
              style={{ background: currentCategory.color }}
            >
              {currentCategory.icon || currentCategory.name.substring(0, 2)}
            </div>
            <h1 className="category-hero-title">{currentCategory.name}</h1>
            <p className="category-hero-description">
              {currentCategory.description || 'Découvrez notre collection'}
            </p>
            <div className="category-hero-stats">
              <div className="stat-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                </svg>
                {products.length} {products.length > 1 ? 'produits' : 'produit'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="category-content">
        <div className="container">
          <div className="category-layout">
            
            {/* Sidebar */}
            <aside className="category-sidebar">
              {/* Categories Navigation */}
              <div className="sidebar-section">
                <h3 className="sidebar-title">Catégories</h3>
                <div className="category-nav-list">
                  {allCategories.map(category => (
                    <button
                      key={category.id}
                      className={`category-nav-item ${
                        category.name.toLowerCase() === categoryName.toLowerCase() ? 'active' : ''
                      }`}
                      onClick={() => handleCategoryChange(category.name)}
                      style={{
                        borderLeftColor: category.name.toLowerCase() === categoryName.toLowerCase() 
                          ? category.color 
                          : 'transparent'
                      }}
                    >
                      <span className="category-nav-icon" style={{ color: category.color }}>
                        {category.icon || '📦'}
                      </span>
                      <span className="category-nav-name">{category.name}</span>
                      <span className="category-nav-count">{category.productCount || 0}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Filter */}
              <div className="sidebar-section">
                <h3 className="sidebar-title">Prix</h3>
                <div className="price-filter">
                  <div className="price-inputs">
                    <input
                      type="number"
                      placeholder="Min"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, min: Number(e.target.value) }))}
                      className="price-input"
                    />
                    <span>-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, max: Number(e.target.value) }))}
                      className="price-input"
                    />
                  </div>
                  <button 
                    className="btn-filter-apply"
                    onClick={fetchData}
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            </aside>

            {/* Products Grid */}
            <div className="category-products">
              {/* Toolbar */}
              <div className="products-toolbar">
                <div className="toolbar-info">
                  <span className="results-count">
                    {sortedProducts.length} {sortedProducts.length > 1 ? 'résultats' : 'résultat'}
                  </span>
                </div>
                <div className="toolbar-sort">
                  <label htmlFor="sort-select">Trier par:</label>
                  <select
                    id="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="default">Par défaut</option>
                    <option value="name-asc">Nom (A-Z)</option>
                    <option value="name-desc">Nom (Z-A)</option>
                    <option value="price-asc">Prix croissant</option>
                    <option value="price-desc">Prix décroissant</option>
                  </select>
                </div>
              </div>

              {/* Products Grid */}
              {sortedProducts.length === 0 ? (
                <div className="no-products">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <h3>Aucun produit trouvé</h3>
                  <p>Essayez d'ajuster vos filtres ou explorez d'autres catégories</p>
                </div>
              ) : (
                <div className="products-grid">
                  {sortedProducts.map(product => (
                    <div key={product.id} className="product-card">
                      <div className="product-image-wrapper">
                        <img
                          src={product.images?.[0] || 'https://via.placeholder.com/300'}
                          alt={product.name}
                          className="product-image"
                        />
                        {product.discount && (
                          <span className="product-badge">-{product.discount}%</span>
                        )}
                      </div>
                      <div className="product-info">
                        <h3 className="product-name">{product.name}</h3>
                        <p className="product-description">
                          {product.description?.substring(0, 80)}...
                        </p>
                        <div className="product-footer">
                          <div className="product-price">
                            <span className="price-current">{formatPrice(product.price)}</span>
                            {product.originalPrice && (
                              <span className="price-original">{formatPrice(product.originalPrice)}</span>
                            )}
                          </div>
                          <button
                            className="btn-view-product"
                            onClick={() => navigate(`/products/${product.id}`)}
                          >
                            Voir détails
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CategoriesPage;